import { useEffect, useState, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import {
  Star, Phone, MessageCircle, MapPin, Clock, ArrowRight, ArrowLeft,
  X, Check, Scissors, Sparkles, Menu
} from "lucide-react";
import {
  NAV, HERO, SERVICES as SIDS, ABOUT, GALLERY, TESTIMONIALS,
  CONTACT, BOOKING
} from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER = "917980162824"; // international format
const PHONE_NUMBER = "07980162824";
const ADDRESS = "MAHI APPARTMENT KANTHADHAR CHARTALA, MORE, Nawabganj, Ichhapur Defence Estate, West Bengal 743144";

const CATEGORIES = ["All", "Hair", "Colour", "Treatments", "Grooming", "Beauty"];

const TESTIMONIALS_DATA = [
  { quote: "Good service and staff behaviour is very good, really satisfied.", author: "Verified Google Review" },
  { quote: "They always offer services that ensure customer satisfaction.", author: "Verified Google Review" },
  { quote: "The balayage came out gorgeous — subtle, luminous, exactly what I wanted.", author: "Anjali S." },
  { quote: "Best beard trim in Nawabganj. Precise and unhurried.", author: "Ranjit D." },
];

const GALLERY_IMAGES = [
  { url: "https://images.unsplash.com/photo-1497433550656-7fb185be365e?w=900&q=80", label: "Balayage" },
  { url: "https://images.unsplash.com/photo-1593702275687-f8b402bf1fb5?w=900&q=80", label: "Beard Craft" },
  { url: "https://images.pexels.com/photos/4783290/pexels-photo-4783290.jpeg?w=900", label: "Hair Spa" },
  { url: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=900&q=80", label: "Colour Studio" },
  { url: "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?w=900&q=80", label: "Bridal" },
  { url: "https://images.unsplash.com/photo-1560869713-7d0a29430803?w=900&q=80", label: "Signature Cuts" },
];

// -------------- Header --------------
function Header({ onBookClick }) {
  const [open, setOpen] = useState(false);
  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <header className="fixed top-0 left-0 right-0 z-50" style={{ background: "rgba(10,10,10,0.7)", backdropFilter: "blur(16px)", borderBottom: "1px solid var(--border)" }}>
      <div className="container-x flex items-center justify-between py-5">
        <a href="#top" data-testid={NAV.logo} className="font-serif text-2xl tracking-wide" style={{ color: "var(--text-main)" }}>
          COLOR <span className="text-gold">&</span> CURLS
        </a>
        <nav className="hidden md:flex items-center gap-10 text-sm" style={{ color: "var(--text-muted)" }}>
          <button data-testid={NAV.linkServices} onClick={() => scrollTo("services")} className="hover:text-white transition-colors uppercase tracking-widest text-xs">Services</button>
          <button data-testid={NAV.linkAbout} onClick={() => scrollTo("about")} className="hover:text-white transition-colors uppercase tracking-widest text-xs">About</button>
          <button data-testid={NAV.linkGallery} onClick={() => scrollTo("gallery")} className="hover:text-white transition-colors uppercase tracking-widest text-xs">Gallery</button>
          <button data-testid={NAV.linkContact} onClick={() => scrollTo("contact")} className="hover:text-white transition-colors uppercase tracking-widest text-xs">Visit</button>
        </nav>
        <div className="flex items-center gap-3">
          <button data-testid={NAV.bookBtn} onClick={onBookClick} className="btn-primary hidden sm:inline-flex" style={{ padding: "0.7rem 1.4rem", fontSize: "0.7rem" }}>
            Book Now
          </button>
          <button onClick={() => setOpen(!open)} className="md:hidden text-white p-2" aria-label="Toggle menu">
            <Menu size={22} />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t" style={{ borderColor: "var(--border)" }}>
          <div className="container-x py-6 flex flex-col gap-4 text-sm" style={{ color: "var(--text-muted)" }}>
            <button onClick={() => scrollTo("services")} className="text-left uppercase tracking-widest text-xs">Services</button>
            <button onClick={() => scrollTo("about")} className="text-left uppercase tracking-widest text-xs">About</button>
            <button onClick={() => scrollTo("gallery")} className="text-left uppercase tracking-widest text-xs">Gallery</button>
            <button onClick={() => scrollTo("contact")} className="text-left uppercase tracking-widest text-xs">Visit</button>
            <button onClick={onBookClick} className="btn-primary mt-2 self-start" style={{ padding: "0.7rem 1.4rem", fontSize: "0.7rem" }}>Book Now</button>
          </div>
        </div>
      )}
    </header>
  );
}

// -------------- Hero --------------
function Hero({ onBookClick }) {
  return (
    <section id="top" data-testid={HERO.root} className="relative min-h-screen flex items-center overflow-hidden grain">
      <div className="hero-bg" />
      <div className="hero-overlay" />
      <div className="container-x relative z-10 pt-32 pb-20 w-full">
        <div className="max-w-3xl">
          <div className="fade-up flex items-center gap-3 mb-8" data-testid={HERO.ratingBadge}>
            <span className="hairline" />
            <span className="flex items-center gap-1" style={{ color: "var(--gold)" }}>
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
            </span>
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--text-muted)" }}>4.8 · 182 Google Reviews</span>
          </div>
          <h1 className="fade-up delay-1 font-serif font-light leading-[0.95] tracking-tight" style={{ fontSize: "clamp(3rem, 8vw, 6.5rem)" }}>
            Colour, curls<br />
            <em className="italic" style={{ color: "var(--gold)" }}>&amp; a little</em> quiet luxury.
          </h1>
          <p className="fade-up delay-2 mt-8 max-w-xl text-lg leading-relaxed" style={{ color: "var(--text-muted)" }}>
            A sanctuary for hair, skin, and stillness in the heart of Ichhapur. Twenty-one crafts, one obsessive standard.
          </p>
          <div className="fade-up delay-3 mt-10 flex flex-wrap gap-4">
            <button data-testid={HERO.bookBtn} onClick={onBookClick} className="btn-primary">
              Book an Appointment <ArrowRight size={16} />
            </button>
            <a data-testid={HERO.servicesBtn} href="#services" className="btn-outline">
              Explore Services
            </a>
          </div>
          <div className="fade-up delay-4 mt-16 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>
            <span className="flex items-center gap-2"><Clock size={14} className="text-gold" /> Open · Closes 9 PM</span>
            <span className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> Nawabganj, WB</span>
            <span className="flex items-center gap-2"><Scissors size={14} className="text-gold" /> 21 Signature Services</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------- Services --------------
function Services({ services, onBook }) {
  const [cat, setCat] = useState("All");
  const filtered = useMemo(() =>
    cat === "All" ? services : services.filter((s) => s.category === cat),
    [cat, services]
  );

  return (
    <section id="services" data-testid={SIDS.root} className="section-y" style={{ background: "var(--bg)" }}>
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="eyebrow">Our Artistry</span>
            <h2 className="font-serif font-light mt-4 tracking-tight" style={{ fontSize: "clamp(2.25rem,5vw,3.75rem)" }}>
              A menu <em style={{ color: "var(--gold)" }}>crafted</em> for you.
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className="px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors"
                style={{
                  borderColor: cat === c ? "var(--gold)" : "var(--border)",
                  color: cat === c ? "var(--gold)" : "var(--text-muted)",
                  background: cat === c ? "rgba(212,175,55,0.06)" : "transparent",
                }}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((s) => (
            <div key={s.id} data-testid={SIDS.card(s.id)} className="lux-card p-7 flex flex-col justify-between">
              <div>
                <span className="chip">{s.category}</span>
                <h3 className="font-serif font-light mt-5 text-2xl leading-tight">{s.name}</h3>
                <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>
                  {s.duration} min · Expert stylist · Post-service consultation.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <span className="font-serif text-2xl text-gold">₹{s.price}</span>
                <button
                  data-testid={SIDS.bookServiceBtn(s.id)}
                  onClick={() => onBook(s.id)}
                  className="text-xs uppercase tracking-[0.2em] hover:text-white transition-colors flex items-center gap-1"
                  style={{ color: "var(--gold)" }}
                >
                  Book <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------------- About --------------
function About() {
  return (
    <section id="about" data-testid={ABOUT.root} className="section-y" style={{ background: "#080808" }}>
      <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/7195799/pexels-photo-7195799.jpeg?w=900"
            alt="Salon interior"
            className="w-full object-cover"
            style={{ height: "540px", filter: "grayscale(15%)" }}
          />
          <div className="absolute -bottom-6 -right-6 p-8 border" style={{ background: "var(--surface)", borderColor: "var(--gold)" }}>
            <div className="font-serif text-5xl text-gold leading-none">4.8</div>
            <div className="text-xs uppercase tracking-[0.2em] mt-2" style={{ color: "var(--text-muted)" }}>from 182 reviews</div>
          </div>
        </div>
        <div>
          <span className="eyebrow">The House</span>
          <h2 className="font-serif font-light mt-4 tracking-tight" style={{ fontSize: "clamp(2rem,4.5vw,3.25rem)" }}>
            A quiet <em style={{ color: "var(--gold)" }}>ritual</em> of grooming.
          </h2>
          <p className="mt-8 text-lg leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Located in the heart of Ichhapur Defence Estate, Color &amp; Curls is a sanctuary for those who appreciate the finer aspects of grooming and self-care. Our stylists blend classical technique with contemporary trend — from balayage to keratin, bridal to beard.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-6">
            <div>
              <div className="font-serif text-4xl text-gold">21</div>
              <div className="text-xs uppercase tracking-[0.2em] mt-2" style={{ color: "var(--text-muted)" }}>Signature services</div>
            </div>
            <div>
              <div className="font-serif text-4xl text-gold">11h</div>
              <div className="text-xs uppercase tracking-[0.2em] mt-2" style={{ color: "var(--text-muted)" }}>Open daily · till 9 PM</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------- Gallery --------------
function Gallery() {
  return (
    <section id="gallery" data-testid={GALLERY.root} className="section-y">
      <div className="container-x">
        <div className="mb-16 max-w-2xl">
          <span className="eyebrow">The Work</span>
          <h2 className="font-serif font-light mt-4 tracking-tight" style={{ fontSize: "clamp(2rem,4.5vw,3.25rem)" }}>
            A gallery of <em style={{ color: "var(--gold)" }}>finished</em> pieces.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {GALLERY_IMAGES.map((g, i) => (
            <div key={i} className={`group relative overflow-hidden ${i === 0 ? "md:row-span-2 md:col-span-1" : ""}`}>
              <img
                src={g.url}
                alt={g.label}
                className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                style={{ height: i === 0 ? "620px" : "300px", filter: "grayscale(10%)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                <span className="font-serif text-2xl">{g.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------------- Testimonials --------------
function Testimonials() {
  return (
    <section data-testid={TESTIMONIALS.root} className="section-y" style={{ background: "#080808" }}>
      <div className="container-x">
        <div className="mb-16 text-center">
          <span className="eyebrow">Kind Words</span>
          <h2 className="font-serif font-light mt-4 tracking-tight" style={{ fontSize: "clamp(2rem,4.5vw,3.25rem)" }}>
            Voices from <em style={{ color: "var(--gold)" }}>the chair.</em>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {TESTIMONIALS_DATA.map((t, i) => (
            <div key={i} className="p-10 border" style={{ borderColor: "var(--border)" }}>
              <span className="font-serif text-6xl text-gold leading-none">&ldquo;</span>
              <p className="font-serif italic text-2xl leading-snug mt-2" style={{ color: "var(--text-main)" }}>
                {t.quote}
              </p>
              <div className="mt-8 flex items-center gap-4">
                <span className="hairline" />
                <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--text-muted)" }}>{t.author}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------------- Contact / Footer --------------
function Contact({ onBookClick }) {
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Color & Curls, I would like to book an appointment.")}`;
  return (
    <section id="contact" data-testid={CONTACT.root} className="section-y">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="eyebrow">Visit</span>
            <h2 className="font-serif font-light mt-4 tracking-tight" style={{ fontSize: "clamp(2.25rem,5vw,4rem)" }}>
              Step inside.<br />
              <em style={{ color: "var(--gold)" }}>Slow down.</em>
            </h2>
            <p className="mt-8 text-lg" style={{ color: "var(--text-muted)" }}>
              Walk-ins welcome, appointments cherished. Reach us anytime — we&apos;ll hold a chair.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={onBookClick} className="btn-primary">Book Online <ArrowRight size={16} /></button>
              <a href={waLink} target="_blank" rel="noreferrer" data-testid={CONTACT.whatsappBtn} className="btn-outline">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a href={`tel:+${WHATSAPP_NUMBER}`} data-testid={CONTACT.callBtn} className="btn-outline">
                <Phone size={16} /> Call
              </a>
            </div>
          </div>
          <div className="space-y-10">
            <div>
              <div className="flex items-center gap-3 mb-3" style={{ color: "var(--gold)" }}>
                <MapPin size={16} /> <span className="text-xs uppercase tracking-[0.25em]">Address</span>
              </div>
              <p className="text-lg leading-relaxed" style={{ color: "var(--text-main)" }}>
                {ADDRESS}
              </p>
            </div>
            <div className="divider" />
            <div>
              <div className="flex items-center gap-3 mb-3" style={{ color: "var(--gold)" }}>
                <Phone size={16} /> <span className="text-xs uppercase tracking-[0.25em]">Phone / WhatsApp</span>
              </div>
              <p className="text-lg" style={{ color: "var(--text-main)" }}>{PHONE_NUMBER}</p>
            </div>
            <div className="divider" />
            <div>
              <div className="flex items-center gap-3 mb-3" style={{ color: "var(--gold)" }}>
                <Clock size={16} /> <span className="text-xs uppercase tracking-[0.25em]">Hours</span>
              </div>
              <p className="text-lg" style={{ color: "var(--text-main)" }}>Open daily · Closes 9:00 PM</p>
            </div>
          </div>
        </div>

        <div className="mt-24 pt-10 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-4" style={{ borderColor: "var(--border)" }}>
          <div className="font-serif text-2xl">COLOR <span className="text-gold">&</span> CURLS</div>
          <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--text-muted)" }}>
            © {new Date().getFullYear()} · Crafted with care in Nawabganj
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------- Booking Modal --------------
const STEPS = ["Service", "Date", "Time", "Details", "Confirm"];

function BookingModal({ open, onClose, services, initialServiceId }) {
  const [step, setStep] = useState(0);
  const [serviceId, setServiceId] = useState(initialServiceId || null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const service = services.find((s) => s.id === serviceId);

  useEffect(() => {
    if (open) {
      setStep(initialServiceId ? 1 : 0);
      setServiceId(initialServiceId || null);
      setDate("");
      setSlots([]);
      setTime(null);
      setName("");
      setPhone("");
      setNotes("");
      setError("");
      setResult(null);
    }
  }, [open, initialServiceId]);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    axios.get(`${API}/availability`, { params: { date } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  if (!open) return null;

  const canNext = () => {
    if (step === 0) return !!serviceId;
    if (step === 1) return !!date;
    if (step === 2) return !!time;
    if (step === 3) return name.trim().length > 1 && /^[0-9+\-\s()]{7,}$/.test(phone.trim());
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const r = await axios.post(`${API}/bookings`, {
        service_id: serviceId, date, time, name: name.trim(), phone: phone.trim(), notes,
      });
      setResult(r.data);
      setStep(4);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid={BOOKING.modal} onClick={(e) => { if (e.target.classList.contains("modal-backdrop")) onClose(); }}>
      <div className="modal-shell p-8 md:p-12">
        <button data-testid={BOOKING.closeBtn} onClick={onClose} className="absolute top-5 right-5 text-white/60 hover:text-white transition-colors" aria-label="Close">
          <X size={22} />
        </button>

        {/* Stepper */}
        {!result && (
          <div data-testid={BOOKING.stepper} className="flex items-center gap-2 mb-10 flex-wrap">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 flex items-center justify-center border text-xs"
                    style={{
                      borderColor: i <= step ? "var(--gold)" : "var(--border)",
                      background: i < step ? "var(--gold)" : "transparent",
                      color: i < step ? "#000" : i === step ? "var(--gold)" : "var(--text-muted)",
                    }}
                  >
                    {i < step ? <Check size={14} /> : i + 1}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] hidden sm:inline" style={{ color: i === step ? "var(--gold)" : "var(--text-muted)" }}>{label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="w-6 h-px" style={{ background: "var(--border)" }} />}
              </div>
            ))}
          </div>
        )}

        {/* Step content */}
        {step === 0 && (
          <div>
            <h3 className="font-serif text-3xl mb-2">Choose a service</h3>
            <p className="text-sm mb-8" style={{ color: "var(--text-muted)" }}>Select the treatment you&apos;d like to book.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[380px] overflow-y-auto pr-2">
              {services.map((s) => (
                <button
                  key={s.id}
                  data-testid={BOOKING.serviceOption(s.id)}
                  onClick={() => setServiceId(s.id)}
                  className="text-left p-4 border transition-colors"
                  style={{
                    borderColor: serviceId === s.id ? "var(--gold)" : "var(--border)",
                    background: serviceId === s.id ? "rgba(212,175,55,0.06)" : "transparent",
                  }}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-serif text-lg leading-tight">{s.name}</div>
                      <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{s.category} · {s.duration} min</div>
                    </div>
                    <div className="text-gold font-serif text-lg">₹{s.price}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h3 className="font-serif text-3xl mb-2">Pick a date</h3>
            <p className="text-sm mb-8" style={{ color: "var(--text-muted)" }}>Choose a day for your {service?.name}.</p>
            <input
              data-testid={BOOKING.dateInput}
              type="date"
              min={today}
              value={date}
              onChange={(e) => { setDate(e.target.value); setTime(null); }}
              className="lux-input text-lg"
              style={{ colorScheme: "dark" }}
            />
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 className="font-serif text-3xl mb-2">Select a time</h3>
            <p className="text-sm mb-8" style={{ color: "var(--text-muted)" }}>Available slots for {date}.</p>
            {loadingSlots ? (
              <p style={{ color: "var(--text-muted)" }}>Loading available times…</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-[320px] overflow-y-auto">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={BOOKING.timeSlot(s.time)}
                    disabled={!s.available}
                    onClick={() => setTime(s.time)}
                    className={`time-slot ${time === s.time ? "selected" : ""}`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 className="font-serif text-3xl mb-2">Your details</h3>
            <p className="text-sm mb-8" style={{ color: "var(--text-muted)" }}>We&apos;ll send a confirmation to your phone.</p>
            <div className="space-y-6">
              <input data-testid={BOOKING.nameInput} className="lux-input" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
              <input data-testid={BOOKING.phoneInput} className="lux-input" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <textarea data-testid={BOOKING.notesInput} className="lux-input resize-none" rows={3} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div className="mt-8 p-5 border" style={{ borderColor: "var(--border)" }}>
              <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "var(--gold)" }}>Booking Summary</div>
              <div className="flex justify-between py-1 text-sm"><span style={{ color: "var(--text-muted)" }}>Service</span><span>{service?.name}</span></div>
              <div className="flex justify-between py-1 text-sm"><span style={{ color: "var(--text-muted)" }}>Date</span><span>{date}</span></div>
              <div className="flex justify-between py-1 text-sm"><span style={{ color: "var(--text-muted)" }}>Time</span><span>{time}</span></div>
              <div className="flex justify-between py-1 text-sm"><span style={{ color: "var(--text-muted)" }}>Total</span><span className="text-gold">₹{service?.price}</span></div>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div data-testid={BOOKING.successRoot} className="text-center py-6">
            <div className="w-16 h-16 mx-auto flex items-center justify-center border" style={{ borderColor: "var(--gold)" }}>
              <Check size={28} style={{ color: "var(--gold)" }} />
            </div>
            <h3 className="font-serif text-4xl mt-8">You&apos;re booked.</h3>
            <p className="mt-4 text-lg" style={{ color: "var(--text-muted)" }}>
              We&apos;ve reserved a chair for you, {result.name.split(" ")[0]}.
            </p>
            <div className="mt-8 inline-block p-6 border text-left" style={{ borderColor: "var(--border)" }}>
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gold)" }}>Confirmation code</div>
              <div data-testid={BOOKING.successCode} className="font-serif text-3xl mt-2">{result.confirmation_code}</div>
              <div className="mt-6 text-sm space-y-1" style={{ color: "var(--text-muted)" }}>
                <div><span className="text-white">{result.service_name}</span></div>
                <div>{result.date} · {result.time}</div>
                <div>{result.phone}</div>
              </div>
            </div>
            <div className="mt-10">
              <button data-testid={BOOKING.successDoneBtn} onClick={onClose} className="btn-primary">Done</button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && step !== 4 && (
          <p data-testid={BOOKING.errorMsg} className="mt-6 text-sm" style={{ color: "var(--error, #EF4444)" }}>{error}</p>
        )}

        {/* Nav */}
        {step < 4 && (
          <div className="mt-10 flex justify-between items-center">
            <button
              data-testid={BOOKING.backBtn}
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              className="btn-outline"
              style={{ opacity: step === 0 ? 0.3 : 1 }}
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < 3 ? (
              <button
                data-testid={BOOKING.nextBtn}
                onClick={() => canNext() && setStep(step + 1)}
                disabled={!canNext()}
                className="btn-primary"
              >
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button
                data-testid={BOOKING.confirmBtn}
                onClick={submit}
                disabled={!canNext() || submitting}
                className="btn-primary"
              >
                {submitting ? "Confirming…" : "Confirm Booking"} <Sparkles size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------- App --------------
function App() {
  const [services, setServices] = useState([]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [initialServiceId, setInitialServiceId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(console.error);
  }, []);

  const openBooking = (sid = null) => {
    setInitialServiceId(sid);
    setBookingOpen(true);
  };

  return (
    <div className="App">
      <Header onBookClick={() => openBooking()} />
      <Hero onBookClick={() => openBooking()} />
      <Services services={services} onBook={(sid) => openBooking(sid)} />
      <About />
      <Gallery />
      <Testimonials />
      <Contact onBookClick={() => openBooking()} />
      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        services={services}
        initialServiceId={initialServiceId}
      />
    </div>
  );
}

export default App;
